import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Progress } from '@/components/ui/progress';
import { Upload } from 'lucide-react';
import { uploadFile, type FileAccess } from '@/lib/upload';

/**
 * Shape consumers (RichTextEditor, MediaLibraryModal) expect from the uploader.
 * Backend's streaming upload endpoint returns only `{ id, url, storagePath, status }`
 * — we enrich it locally with metadata we already know about the selected File.
 */
interface UploadedFile {
  id: number;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
}

interface MediaUploaderProps {
  onUploadComplete?: (file: UploadedFile) => void;
  access?: FileAccess;
}

export const MediaUploader: React.FC<MediaUploaderProps> = ({
  onUploadComplete,
  access = 'PUBLIC',
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) {
        return;
      }

      setUploading(true);
      setProgress(0);

      try {
        const result = await uploadFile({
          file,
          access,
          onProgress: ({ percent }) => setProgress(percent),
        });
        onUploadComplete?.({
          id: result.id,
          url: result.url,
          originalName: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
        });
      } catch (error: unknown) {
        console.error('Upload failed', error);
      } finally {
        setUploading(false);
      }
    },
    [access, onUploadComplete],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <div className="w-full">
      <div 
        {...getRootProps()} 
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary'}
        `}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-10 w-10 text-gray-400" />
          <p className="text-sm text-gray-600">
            {isDragActive ? "Drop the file here" : "Drag & drop or click to select"}
          </p>
        </div>
      </div>

      {uploading && (
        <div className="mt-4 space-y-2">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Uploading...</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}
    </div>
  );
};
